import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, MessageCircle, ArrowLeft, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getWsUrl() {
  const base = API_URL || window.location.origin;
  return base.replace(/^http/, 'ws') + '/api/ws';
}

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(searchParams.get('id') || null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const activeConvIdRef = useRef(activeConvId);

  // Keep ref in sync
  useEffect(() => { activeConvIdRef.current = activeConvId; }, [activeConvId]);

  // WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem('vwatch_token');
    if (!token) return;

    let ws;
    let reconnectTimer;

    function connect() {
      ws = new WebSocket(`${getWsUrl()}?token=${token}`);

      ws.onopen = () => {
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'new_message') {
            // If message is for the active conversation, append it
            if (data.conversationId === activeConvIdRef.current) {
              setMessages(prev => {
                // Avoid duplicates (we already optimistically added our own message)
                if (prev.some(m => m.id === data.message.id)) return prev;
                return [...prev, data.message];
              });
            }
            // Update conversation list preview
            setConversations(prev => prev.map(c =>
              c.id === data.conversationId
                ? { ...c, lastMessage: data.message.content, lastMessageAt: data.message.createdAt }
                : c
            ));
          }

          if (data.type === 'conversation_update') {
            // New conversation created — refresh list
            fetchConversations();
          }

          if (data.type === 'customer_cleared') {
            const myId = user?.id || user?._id;
            if (data.buyerId && myId && data.buyerId !== myId) return;

            const clearedIds = Array.isArray(data.conversationIds) ? data.conversationIds : [];

            setConversations((prev) => prev.filter((c) => !clearedIds.includes(c.id)));

            if (activeConvIdRef.current && clearedIds.includes(activeConvIdRef.current)) {
              setActiveConvId(null);
              setMessages([]);
              navigate('/messages', { replace: true });
            }

            toast.success('Chats cleared by admin. You can start a fresh inquiry now.');
          }
        } catch (e) {}
      };

      ws.onclose = () => {
        setWsConnected(false);
        // Reconnect after 3 seconds
        reconnectTimer = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    }

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => { fetchConversations(); }, []);

  useEffect(() => {
    if (activeConvId) fetchMessages(activeConvId);
  }, [activeConvId]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/conversations`);
      setConversations(res.data);
      if (!activeConvId && res.data.length > 0) setActiveConvId(res.data[0].id);
    } catch (e) {
      console.error('Error fetching conversations:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const res = await axios.get(`${API_URL}/api/conversations/${convId}/messages`);
      setMessages(res.data);
    } catch (e) {
      console.error('Error fetching messages:', e);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConvId) return;
    const content = newMessage.trim();
    setSending(true);
    setNewMessage('');
    try {
      const res = await axios.post(`${API_URL}/api/conversations/${activeConvId}/messages`, { content });
      // Optimistic update — add to messages if not already there via WS
      setMessages(prev => {
        if (prev.some(m => m.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setConversations(prev => prev.map(c =>
        c.id === activeConvId ? { ...c, lastMessage: content, lastMessageAt: new Date().toISOString() } : c
      ));
    } catch (e) {
      toast.error('Failed to send message');
      setNewMessage(content); // Restore message on failure
    } finally {
      setSending(false);
    }
  };

  const activeConv = conversations.find(c => c.id === activeConvId);

  const statusBadge = (conv) => {
    const status = conv?.status;

    // If this conversation is sold, show a clearer label for the buyer
    if (status === 'sold') {
      const isBuyer = conv?.buyerId && user?.id && String(conv.buyerId) === String(user.id);
      const label = isBuyer ? 'Purchased' : 'Sold to Another Buyer';
      const className = isBuyer
        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
        : 'bg-red-500/20 text-red-400 border-red-500/30';

      return (
        <span className={`text-xs px-2 py-1 rounded-full border ${className}`}>
          {label}
        </span>
      );
    }

    const config = {
      active: { label: 'In Discussion', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
      cancelled: { label: 'Cancelled', className: 'bg-red-500/20 text-red-400 border-red-500/30' }
    };

    const badge = config[status];
    if (!badge) return null;

    return (
      <span className={`text-xs px-2 py-1 rounded-full border ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="messages-page">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1280px]">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-foreground tracking-tight font-semibold">Messages</h1>
            <p className="text-muted-foreground mt-1">Your watch inquiries and conversations</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs" data-testid="ws-status">
            {wsConnected ? (
              <><Wifi className="h-3.5 w-3.5 text-emerald-400" /><span className="text-emerald-400">Live</span></>
            ) : (
              <><WifiOff className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-muted-foreground">Connecting...</span></>
            )}
          </div>
        </div>

        {conversations.length === 0 ? (
          <Card className="p-12 text-center border-border bg-card" data-testid="messages-empty-state">
            <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No conversations yet</h3>
            <p className="text-muted-foreground mb-4">Contact a seller on any watch listing to start a conversation.</p>
            <Button onClick={() => navigate('/catalog')} data-testid="messages-browse-button">Browse Watches</Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[340px,1fr] gap-6" style={{ height: 'calc(100vh - 200px)', minHeight: '550px' }}>
            {/* Conversations List */}
            <Card className="border-border bg-card overflow-hidden flex flex-col" data-testid="conversations-list">
              <div className="p-4 border-b border-border">
                <h2 className="font-medium text-foreground text-sm">Your Inquiries</h2>
              </div>
              <div className="flex-1 overflow-y-auto">
                {conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => setActiveConvId(conv.id)}
                    className={`w-full text-left p-4 border-b border-border/50 transition-colors hover:bg-muted/30 ${activeConvId === conv.id ? 'bg-muted/50 border-l-2 border-l-primary' : ''}`}
                    data-testid={`conversation-item-${conv.id}`}
                  >
                    <div className="flex gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#0B1220] flex-shrink-0">
                        <img src={conv.listingImage || 'https://images.unsplash.com/photo-1589688934051-ca9059383f4f?w=200'} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-medium text-foreground truncate">{conv.listingTitle}</h3>
                          {conv.unreadCount > 0 && (
                            <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center flex-shrink-0">{conv.unreadCount}</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.lastMessage || 'New inquiry'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {statusBadge(conv)}
                          <span className="text-[10px] text-muted-foreground">{formatTime(conv.lastMessageAt)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>

            {/* Chat Panel */}
            <Card className="border-border bg-card overflow-hidden flex flex-col" data-testid="chat-panel">
              {activeConv ? (
                <>
                  <div className="p-4 border-b border-border flex items-center gap-3">
                    <button className="lg:hidden p-1" onClick={() => setActiveConvId(null)}>
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#0B1220] flex-shrink-0 cursor-pointer" onClick={() => navigate(`/watch/${activeConv.listingId}`)}>
                      <img src={activeConv.listingImage || 'https://images.unsplash.com/photo-1589688934051-ca9059383f4f?w=200'} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-foreground truncate cursor-pointer hover:text-primary" onClick={() => navigate(`/watch/${activeConv.listingId}`)}>
                        {activeConv.listingTitle}
                      </h3>
                      <p className="text-xs text-muted-foreground">${activeConv.listingPrice?.toLocaleString()}</p>
                    </div>
                    {statusBadge(activeConv)}
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-container">
                    {messages.map(msg => (
                      msg.type === 'system' ? (
                        <div key={msg.id} className="flex justify-center" data-testid="system-message">
                          <div className="bg-muted/50 rounded-full px-4 py-1.5 text-xs text-muted-foreground text-center max-w-md">
                            {msg.content}
                          </div>
                        </div>
                      ) : (
                        <div key={msg.id} className={`flex ${msg.senderId === user?.id ? 'justify-end' : 'justify-start'}`} data-testid="user-message">
                          <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${msg.senderId === user?.id ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted text-foreground rounded-bl-md'}`}>
                            {msg.senderId !== user?.id && <p className="text-[10px] font-medium mb-1 opacity-70">{msg.senderName}</p>}
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-[10px] mt-1 ${msg.senderId === user?.id ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>{formatTime(msg.createdAt)}</p>
                          </div>
                        </div>
                      )
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="p-4 border-t border-border" data-testid="message-input-area">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        disabled={sending}
                        data-testid="message-input"
                        className="flex-1"
                      />
                      <Button onClick={sendMessage} disabled={sending || !newMessage.trim()} size="icon" data-testid="send-message-button">
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select a conversation to view messages</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
